/*
 * Small pieces for step-by-step pages and summaries (docs/dev/ui-guidelines.md): a step indicator,
 * a row of stat tiles, and a status badge. Kept apart from ui.jsx so its API stays as it is.
 */
import { useT } from '../lib/i18n.js';

/**
 * The step indicator of a wizard: an ordered list, the current step marked with
 * `aria-current="step"`. `steps`: [{ key, label }]; `current`: the key of the step on screen. Done
 * steps get a check mark. Purely visual: moving between steps is up to the page's buttons.
 */
export function Steps({ steps, current, label, className = '' }) {
  const t = useT();
  const at = steps.findIndex((s) => s.key === current);
  return (
    <nav aria-label={label ?? t('steps.label')} className={`mb-5 ${className}`}>
      <ol className="flex flex-wrap items-center gap-x-2 gap-y-2 text-sm">
        {steps.map((s, i) => {
          const state = i < at ? 'done' : i === at ? 'current' : 'todo';
          return (
            <li key={s.key} className="flex items-center gap-2" aria-current={state === 'current' ? 'step' : undefined}>
              {i > 0 ? <span aria-hidden="true" className="hidden h-px w-6 bg-line-strong sm:block" /> : null}
              <span
                aria-hidden="true"
                className={`inline-flex size-6 shrink-0 items-center justify-center rounded-full border text-xs font-bold ${
                  state === 'current'
                    ? 'border-accent bg-accent text-on-accent'
                    : state === 'done'
                      ? 'border-kelp bg-kelp/15 text-kelp'
                      : 'border-line-strong text-muted'
                }`}
              >
                {state === 'done' ? (
                  <svg viewBox="0 0 16 16" className="size-3.5" fill="none" stroke="currentColor" strokeWidth="2.2">
                    <path d="M3.5 8.5l3 3 6-7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : (
                  i + 1
                )}
              </span>
              <span className={state === 'current' ? 'font-semibold' : 'text-muted'}>
                {s.label}
                {state === 'done' ? <span className="sr-only"> {t('steps.done')}</span> : null}
              </span>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

const TILE_TONES = {
  default: 'text-text',
  ok: 'text-kelp',
  warn: 'text-amber',
  danger: 'text-danger',
  muted: 'text-muted',
};

/**
 * A row of numbers ("12 servers · 3 blocked"): `items` = [{ label, value, tone?, hidden? }]. Wraps
 * into two columns on phones. A <dl>, so screen readers read "label: value".
 */
export function StatTiles({ items, className = '' }) {
  return (
    <dl className={`grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6 ${className}`}>
      {items
        .filter((i) => i && !i.hidden)
        .map((i) => (
          <div key={i.label} className="flex flex-col rounded-md border border-line bg-sunk/40 px-3 py-2">
            <dt className="order-2 text-xs text-muted">{i.label}</dt>
            <dd className={`order-1 text-xl leading-tight font-[650] ${TILE_TONES[i.tone] ?? TILE_TONES.default}`}>
              {i.value}
            </dd>
          </div>
        ))}
    </dl>
  );
}

const BADGE_TONES = {
  default: ['border-line-strong', 'bg-muted'],
  ok: ['border-kelp/60 bg-kelp/10', 'bg-kelp'],
  info: ['border-accent/50 bg-accent/10', 'bg-accent'],
  warn: ['border-amber/60 bg-amber/10', 'bg-amber'],
  danger: ['border-danger/60 bg-danger/10', 'bg-danger'],
};

/**
 * A short state label in a pill ("Create", "Blocked", "Copying"). The text stays in the body colour
 * (coloured text on a tinted pill misses the contrast ratio); the tone shows in the frame and a dot.
 */
export function Badge({ tone = 'default', children, className = '' }) {
  const [box, dot] = BADGE_TONES[tone] ?? BADGE_TONES.default;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-semibold whitespace-nowrap text-text ${box} ${className}`}
    >
      <span aria-hidden="true" className={`size-1.5 rounded-full ${dot}`} />
      {children}
    </span>
  );
}

/** A thin progress bar with its percentage for screen readers. `value`: 0–100 or null (unknown). */
export function ProgressBar({ value, label = 'Progress', className = '' }) {
  const known = Number.isFinite(value);
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={known ? value : undefined}
      className={`h-1.5 w-full overflow-hidden rounded-full bg-line ${className}`}
    >
      <div
        className={`h-full rounded-full bg-accent transition-[width] ${known ? '' : 'w-1/3 animate-pulse'}`}
        style={known ? { width: `${value}%` } : undefined}
      />
    </div>
  );
}
